import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  CircularProgress,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Cancel } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, oneLight, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import chatApi from '../../api/chatApi';
import knowledgeBaseApi from '../../api/knowledgeBaseApi';
import modelsApi from '../../api/modelsApi';
import LaunchIcon from '@mui/icons-material/Launch';
import { useLocation } from 'react-router-dom';

// Error Boundary 컴포넌트
class MarkdownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Markdown Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
            ⚠️ 마크다운 렌더링 중 오류가 발생했습니다. 원본 텍스트를 표시합니다.
          </Typography>
          <Typography variant="body1" component="div" style={{ whiteSpace: 'pre-wrap' }}>
            {this.props.content}
          </Typography>
        </div>
      );
    }

    return this.props.children;
  }
}

// 유틸리티 함수들을 컴포넌트 외부로 이동
// 콘텐츠에 HTML이 포함되어 있는지 확인
const hasHtmlTags = (content) => {
  if (!content) return false;
  
  // 일반적인 HTML 태그들을 더 정확히 감지
  const htmlTagPattern = /<\/?(?:div|span|p|strong|em|b|i|u|br|hr|h[1-6]|ul|ol|li|table|tr|td|th|thead|tbody|a|img|code|pre|blockquote|section|article|aside|nav|header|footer|main)\b[^>]*>/i;
  
  // 인라인 스타일을 포함한 태그들도 감지
  const styleTagPattern = /<[^>]+style\s*=\s*["'][^"']*["'][^>]*>/i;
  
  return htmlTagPattern.test(content) || styleTagPattern.test(content);
};

// 마크다운 테이블이 있는지 확인
const hasMarkdownTable = (content) => {
  if (!content || typeof content !== 'string') return false;
  
  const lines = content.split('\n');
  
  // 더 간단하고 확실한 테이블 감지
  for (let i = 0; i < lines.length - 1; i++) {
    const currentLine = lines[i].trim();
    const nextLine = lines[i + 1] ? lines[i + 1].trim() : '';
    
    // 현재 줄이 파이프로 시작하고 끝나며, 최소 2개의 파이프가 있는 경우
    if (currentLine.startsWith('|') && currentLine.endsWith('|') && 
        (currentLine.match(/\|/g) || []).length >= 3) {
      
      // 다음 줄이 구분자 행인지 확인 (파이프와 하이픈, 콜론만 포함)
      if (nextLine.startsWith('|') && nextLine.endsWith('|') && 
          nextLine.includes('-') && 
          /^[\|\-\s:]+$/.test(nextLine)) {
        return true;
      }
    }
  }
  
  return false;
};

// 내용이 해시태그로 시작하는지 확인하는 함수
const hasMarkdownHeadings = (content) => {
  if (!content) return false;
  return /(?:^|\n)#+\s+/m.test(content);
};

// 마크다운 테이블을 HTML 테이블로 변환
const convertToHtmlTable = (tableLines) => {
  if (tableLines.length < 3) return tableLines.join('\n'); // 최소 헤더, 구분자, 데이터 행이 필요
  
  // 셀 내용에서 - 항목들을 줄바꿈으로 분리하는 함수
  const processCellContent = (content) => {
    // 여러 패턴의 - 구분자를 처리
    let processed = content;
    
    // 가장 간단한 방법: 모든 " - "를 "<br>- "로 교체
    processed = processed.replace(/\s-\s/g, '<br>- ');
    
    return processed;
  };
  
  // 헤더 추출
  const headerCells = tableLines[0]
    .split('|')
    .filter(cell => cell.trim() !== '')
    .map(cell => cell.trim());
  
  // 데이터 행 추출
  const rows = tableLines.slice(2).map(line => 
    line.split('|')
      .filter(cell => cell.trim() !== '')
      .map(cell => processCellContent(cell.trim()))
  );
  
  // HTML 테이블 생성 - 인라인 스타일 적용
  let htmlTable = '<table style="width:100%; border-collapse:collapse; margin:16px 0; border:1px solid #e0e0e0; border-radius:4px; overflow:hidden; box-shadow:0 2px 4px rgba(0,0,0,0.1);">';
  
  // 헤더 추가
  htmlTable += '<thead style="background-color:#f5f5f5;"><tr>';
  headerCells.forEach(header => {
    htmlTable += `<th style="padding:12px 16px; text-align:left; border-bottom:2px solid #e0e0e0; font-weight:bold;">${header}</th>`;
  });
  htmlTable += '</tr></thead>';
  
  // 데이터 행 추가
  htmlTable += '<tbody>';
  rows.forEach((row, rowIndex) => {
    // 홀수/짝수 행 배경색 지정
    const bgColor = rowIndex % 2 === 0 ? '#ffffff' : '#fafafa';
    htmlTable += `<tr style="background-color:${bgColor};">`;
    row.forEach(cell => {
      htmlTable += `<td style="padding:8px 16px; border-bottom:1px solid #eeeeee;">${cell}</td>`;
    });
    htmlTable += '</tr>';
  });
  htmlTable += '</tbody></table>';
  
  return htmlTable;
};

// 마크다운 테이블 감지 및 변환 함수
const parseMarkdownTables = (content) => {
  if (!content || !content.includes('|')) return content;
  
  // 한 줄로 연결된 테이블을 직접 처리
  const fixInlineTable = (text) => {
    // 매우 간단한 접근: 특정 패턴들을 직접 찾아서 교체
    let result = text;
    
    // 패턴 1: | 기능 | 설명 | |------|------| | ... | ... |
    const patterns = [
      // 일반적인 한줄 테이블 패턴
      /\|\s*([^|]+)\s*\|\s*([^|]+)\s*\|\s*\|[-:\s]+\|[-:\s]+\|\s*((?:\|\s*[^|]+\s*\|\s*[^|]+\s*\|\s*?)+)/g,
      // 더 관대한 패턴
      /\|\s*(.+?)\s*\|\s*(.+?)\s*\|\s*\|[-:\s]+\|[-:\s]+\|\s*((?:\|\s*.+?\s*\|\s*.+?\s*\|\s*?)+)/g
    ];
    
    patterns.forEach(pattern => {
      result = result.replace(pattern, (match, header1, header2, dataSection) => {
        try {
          // 헤더 정리
          const h1 = header1.trim();
          const h2 = header2.trim();
          
          // 데이터 섹션 파싱
          const allCells = dataSection.split('|')
            .map(cell => cell.trim())
            .filter(cell => cell.length > 0);
          
          if (allCells.length < 2) return match;
          
          // 테이블 재구성
          const tableRows = [];
          tableRows.push(`| ${h1} | ${h2} |`);
          tableRows.push('|------|------|');
          
          // 데이터 행들 생성
          for (let i = 0; i < allCells.length; i += 2) {
            if (i + 1 < allCells.length) {
              tableRows.push(`| ${allCells[i]} | ${allCells[i + 1]} |`);
            }
          }
          
          return tableRows.join('\n');
        } catch (e) {
          return match;
        }
      });
    });
    
    return result;
  };
  
  // 인라인 테이블 수정
  content = fixInlineTable(content);
  
  const lines = content.split('\n');
  let result = [];
  let tableLines = [];
  let inTable = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 테이블 시작 감지: 첫 행이 |로 시작하고 끝나며, 다음 행이 구분자 행인지 확인
    if (!inTable && line.startsWith('|') && line.endsWith('|')) {
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trim();
        if (nextLine.startsWith('|') && nextLine.endsWith('|') && nextLine.includes('-')) {
          inTable = true;
          tableLines = [line];
          continue;
        }
      }
    }
    
    // 테이블 내부 처리
    if (inTable) {
      if (line.startsWith('|') && line.endsWith('|')) {
        tableLines.push(line);
      } else {
        // 테이블 종료, HTML 테이블로 변환
        result.push(convertToHtmlTable(tableLines));
        inTable = false;
        tableLines = [];
        if (line) result.push(line); // 빈 줄이 아니라면 추가
      }
    } else {
      result.push(line);
    }
  }
  
  // 마지막 테이블 처리
  if (inTable && tableLines.length > 0) {
    result.push(convertToHtmlTable(tableLines));
  }
  
  return result.join('\n');
};

// 마크다운 헤딩 처리 (### 형식을 적절한 헤딩으로 변환)
const processMarkdownHeadings = (content) => {
  if (!content) return content;
  
  // 이미 처리된 HTML이면서 변환이 필요없는 경우 건너뜀
  if (hasHtmlTags(content) && !hasMarkdownTable(content) && !content.includes('#') && !content.includes('**')) return content;
  
  // 마크다운 형식 관련 설명 텍스트 제거
  let processedContent = content.replace(/이 표는 마크다운 형식으로 작성되었습니다\./g, '');
  processedContent = processedContent.replace(/마크다운 형식(으로|의|을|을 이용하여|을 사용하여).*?작성/g, '작성');
  
  // - 로 시작하는 항목들을 줄바꿈으로 분리 (더 간단한 방법)
  processedContent = processedContent.replace(/\s+-\s+/g, '\n\n- ');
  
  // 테이블 영역과 비-테이블 영역을 분리하여 처리
  if (hasMarkdownTable(processedContent)) {
    // 줄 단위로 처리
    const lines = processedContent.split('\n');
    let inTable = false;
    let tableStart = 0;
    const processedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      
      // 테이블 시작 감지
      if (!inTable && line.startsWith('|') && line.endsWith('|') && 
          nextLine.startsWith('|') && nextLine.endsWith('|') && nextLine.includes('-')) {
        inTable = true;
        tableStart = i;
        processedLines.push(lines[i]); // 현재 줄 추가
        continue;
      }
      
      // 테이블 종료 감지
      if (inTable && (!line.startsWith('|') || !line.endsWith('|'))) {
        inTable = false;
        // 테이블 전체를 처리하여 HTML로 변환
        const tableLines = lines.slice(tableStart, i);
        processedLines.pop(); // 마지막으로 추가한 테이블 줄 제거
        processedLines.push(convertToHtmlTable(tableLines));
      }
      
      // 테이블 내부 줄은 그대로 추가
      if (inTable) {
        processedLines.push(lines[i]);
        continue;
      }
      
      // 테이블 외부 영역에서 헤더 및 볼드체 처리
      let processedLine = line;
      
      // 헤더 처리
      if (line.match(/^#{1,6}\s+/)) {
        const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headerMatch) {
          const hashes = headerMatch[1];
          const text = headerMatch[2];
          const level = Math.min(hashes.length, 6);
          
          const styles = {
            1: "font-size:20px; font-weight:bold; color:#2c3e50; margin-top:20px; margin-bottom:10px; display:block;",
            2: "font-size:18px; font-weight:bold; color:#2c3e50; margin-top:16px; margin-bottom:8px; display:block;",
            3: "font-size:16px; font-weight:bold; color:#2c3e50; margin-top:14px; margin-bottom:7px; display:block;"
          };
          
          processedLine = `<div style="${styles[level]}">${text}</div>`;
        }
      } 
      // 볼드체 처리 (**text**)
      else if (line.includes('**')) {
        processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
      }
      
      processedLines.push(processedLine);
    }
    
    // 마지막 테이블 처리
    if (inTable) {
      const tableLines = lines.slice(tableStart);
      // 이미 추가된 테이블 줄 제거
      processedLines.splice(processedLines.length - (lines.length - tableStart), lines.length - tableStart);
      processedLines.push(convertToHtmlTable(tableLines));
    }
    
    let joinedContent = processedLines.join('\n');
    // 테이블 밖에 있는 볼드체 처리 한번 더 수행
    joinedContent = joinedContent.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
    return joinedContent;
  } else {
    // 테이블이 없는 경우 헤더와 볼드체 처리
    // 먼저 헤더 처리
    let result = processedContent.replace(/^(#{1,6})\s+(.+)$/gm, (match, hashes, text) => {
      const level = Math.min(hashes.length, 6);
      
      const styles = {
        1: "font-size:20px; font-weight:bold; color:#2c3e50; margin-top:20px; margin-bottom:10px; display:block;",
        2: "font-size:18px; font-weight:bold; color:#2c3e50; margin-top:16px; margin-bottom:8px; display:block;",
        3: "font-size:16px; font-weight:bold; color:#2c3e50; margin-top:14px; margin-bottom:7px; display:block;"
      };
      
      return `<div style="${styles[level]}">${text}</div>`;
    });
    
    // 볼드체 처리
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
    
    return result;
  }
};

const ChatPage = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKBs, setSelectedKBs] = useState([]);
  const [useStreaming, setUseStreaming] = useState(
    process.env.REACT_APP_ENABLE_STREAMING === 'true'
  );
  const [llmModels, setLlmModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const chatEndRef = useRef(null);
  const [isKBLoading, setIsKBLoading] = useState(true);
  const [isModelsLoading, setIsModelsLoading] = useState(true);

  // 제품 선택 버튼 상태 관리
  // const [selectedProduct, setSelectedProduct] = useState('AUIGrid');
  // const products = [
  //   { id: '전체', label: '전체', prefix: '' },
  //   { id: 'AUIGrid', label: 'AUIGrid', prefix: 'AUIGrid 에서 ' },
  //   { id: 'AUIPivot', label: 'AUIPivot', prefix: 'AUIPivot 에서 ' },
  //   { id: 'AUIGantt', label: 'AUIGantt', prefix: 'AUIGantt 에서 ' }
  // ];
  
  // 알림창 상태 관리
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const location = useLocation();
  
  // 카카오톡 연동 관련 상태
  const [isFromKakao, setIsFromKakao] = useState(false);
  const [kakaoAnswerId, setKakaoAnswerId] = useState(null);
  const [isLoadingKakaoData, setIsLoadingKakaoData] = useState(false);
  const [kakaoDataLoaded, setKakaoDataLoaded] = useState(false);

  // 알림창 표시 함수
  const showAlert = (message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  };

  // 알림창 닫기 함수
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  // 카카오톡 답변 데이터 로드 함수
  const loadKakaoAnswerData = async (answerId) => {
    try {
      setIsLoadingKakaoData(true);
      console.log(`[카카오톡 연동] 답변 데이터 로드 시작: ${answerId}`);
      
      const response = await fetch(`https://gpt.buildupkorea.com:8800/api/chat/answer-data/${answerId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const answerData = await response.json();
      console.log('[카카오톡 연동] 답변 데이터 로드 완료:', answerData);
      
      // 채팅창에 카카오톡 질문과 답변 추가
      const kakaoMessages = [
        {
          role: 'user',
          content: answerData.question,
          isFromKakao: true,
          timestamp: answerData.created_at
        },
        {
          role: 'assistant',
          content: answerData.answer,
          isFromKakao: true,
          sources: answerData.sources || [],
          processingTime: answerData.processing_time,
          timestamp: answerData.created_at
        }
      ];
      
      setMessages(kakaoMessages);
      setSourceDocuments(answerData.sources || []);
      setKakaoDataLoaded(true);
      
      // 성공 알림 표시
      showAlert(`카카오톡에서 요청하신 "${answerData.question}" 질문의 답변을 불러왔습니다. 추가 질문을 계속하실 수 있습니다.`);
      
    } catch (error) {
      console.error('[카카오톡 연동] 답변 데이터 로드 실패:', error);
      setError(`카카오톡 답변을 불러올 수 없습니다: ${error.message}`);
      showAlert('카카오톡 답변을 불러오는 중 오류가 발생했습니다. 새로고침 후 다시 시도해주세요.');
    } finally {
      setIsLoadingKakaoData(false);
    }
  };

  // URL 파라미터 확인 및 카카오톡 데이터 로드
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromKakao = urlParams.get('from_kakao');
    const answerId = urlParams.get('answer_id');
    
    console.log('[카카오톡 연동] URL 파라미터 확인:', { fromKakao, answerId });
    
    if (fromKakao === 'true' && answerId && !kakaoDataLoaded) {
      setIsFromKakao(true);
      setKakaoAnswerId(answerId);
      loadKakaoAnswerData(answerId);
    }
  }, [location.search, kakaoDataLoaded]);

  useEffect(() => {
    // 지식베이스 목록 가져오기
    const fetchKnowledgeBases = async () => {
      try {
        setIsKBLoading(true);
        const response = await knowledgeBaseApi.listKnowledgeBases();
        // 준비된 지식베이스만 필터링
        const readyKBs = response.knowledge_bases.filter(kb => kb.status === 'ready');
        console.log('=== 지식베이스 로딩 결과 ===');
        console.log('전체 지식베이스:', response.knowledge_bases);
        console.log('준비된 지식베이스:', readyKBs);
        console.log('===============================');
        
        setKnowledgeBases(readyKBs);
        if (readyKBs.length > 0) {
          // 첫 번째 KB를 기본 선택으로 설정 (배열에 넣음)
          const defaultKBId = readyKBs[0].kb_id;
          console.log('기본 선택할 지식베이스 ID:', defaultKBId);
          setSelectedKBs([defaultKBId]);
        } else {
          console.log('준비된 지식베이스가 없습니다!');
        }
      } catch (error) {
        console.error('지식베이스를 가져오는 중 오류 발생:', error);
        setError('지식베이스 목록을 불러올 수 없습니다.');
      } finally {
        setIsKBLoading(false);
      }
    };

    // LLM 모델 목록 가져오기
    const fetchLLMModels = async () => {
      try {
        setIsModelsLoading(true);
        const response = await modelsApi.listLlmModels();
        setLlmModels(response.models);
      } catch (error) {
        console.error('LLM 모델을 가져오는 중 오류 발생:', error);
        setError('LLM 모델 목록을 불러올 수 없습니다.');
      } finally {
        setIsModelsLoading(false);
      }
    };

    fetchKnowledgeBases();
    fetchLLMModels();
  }, []);

  useEffect(() => {
    // 지식베이스나 모델이 변경될 때만 대화 내용 초기화
    setInput('');
    setMessages([]);
    setSourceDocuments([]);
    console.log("지식베이스 또는 모델 변경으로 대화 내용 초기화");
  }, [selectedKBs, selectedModel]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter는 줄바꿈 (기본 동작 유지)
        return;
      }
      
      // Enter만 누를 경우 폼 제출
      e.preventDefault();
      if (input.trim()) {
        handleSubmit(e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // 디버깅: 지식베이스 선택 상태 확인
    console.log('=== 채팅 전송 시 지식베이스 상태 ===');
    console.log('selectedKBs:', selectedKBs);
    console.log('selectedKBs.length:', (selectedKBs || []).length);
    console.log('knowledgeBases:', knowledgeBases);
    console.log('========================================');

    // 지식베이스 선택 확인
    if (!(selectedKBs || []).length) {
      showAlert('하나 이상의 지식베이스를 선택해주세요. 지식베이스 없이는 질문에 답변할 수 없습니다.');
      return;
    }

    // 선택된 제품에 따라 질문 앞에 prefix 추가 (LLM 전송용)
    // const selectedProductData = products.find(p => p.id === selectedProduct);
    // const prefixedInput = selectedProductData?.prefix ? selectedProductData.prefix + input : input;
    
    // 사용자가 입력한 원본 텍스트를 채팅창에 표시
    const userMessage = { role: 'user', content: input };
    
    // 기존 메시지를 유지하면서 새 메시지 추가
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setSourceDocuments([]);
    
    try {
      setIsLoading(true);
      setError(null);

      // 요청에 타임스탬프와 고유 ID를 추가하여 캐싱 방지
      const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // 이전 대화 내역을 포함하여 문맥 유지 (최대 5개 메시지로 제한)
      // 새로운 사용자 메시지를 추가하기 전의 메시지들을 계산
      const previousMessages = messages.slice(-10);
      
      // LLM에 전송할 메시지 (prefix가 추가된 내용 사용)
      const llmMessage = { role: 'user', content: input };
      
      const chatRequest = {
        query: input,  // LLM에는 prefix가 포함된 질문 전달
        kb_ids: selectedKBs,
        model: selectedModel,
        use_streaming: useStreaming,
        session_id: null,
        selected_product: null,  // 제품 정보 제거
        maintain_context: false,
        messages: [
          ...messages,
          userMessage
        ]
      };

      console.log(`요청 시작 ID: ${requestId}, 타임스탬프: ${chatRequest.timestamp}, 메시지 수: ${chatRequest.messages.length}`);

      if (useStreaming) {
        // 스트리밍 처리
        let assistantResponse = '';
        let tempId = Date.now().toString();
        
        // 임시 메시지 추가
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: 'assistant', content: '', id: tempId },
        ]);

        // 스트림 연결 저장 변수
        let streamConnection;

        try {
          streamConnection = await chatApi.sendStreamingChatMessage(
            chatRequest,
            (data) => {
              // 응답 메시지 처리
              assistantResponse = data.response;
              
              // 메시지 업데이트
              setMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg.id === tempId ? { ...msg, content: assistantResponse } : msg
                )
              );

              // 소스 문서 처리
              if (data.sources) {
                console.debug('스트리밍 응답에서 참조문서 수신:', data.sources?.length || 0);
                // 전체 소스 문서 정보 보존
                const processedSources = data.sources.map(source => ({
                  ...source,
                  // source_url 또는 url 필드 확인 (크롤링 시 url 필드로 저장될 수 있음)
                  source_url: (source.source_url && 
                    (source.source_url.startsWith('http://') || source.source_url.startsWith('https://'))) 
                      ? source.source_url 
                      : (source.url && 
                        (source.url.startsWith('http://') || source.url.startsWith('https://'))) 
                        ? source.url 
                        : null
                }));
                setSourceDocuments(processedSources);
              }
            },
            (error) => {
              console.error(`스트리밍 에러 (요청 ID: ${requestId}):`, error);
              setError('응답을 받는 중 오류가 발생했습니다.');
              setIsLoading(false);
            },
            () => {
              // 스트리밍 완료 후 처리
              setIsLoading(false);
              
              // 스트리밍 완료 후 약간의 시간을 두고 마크다운 헤딩 처리
              setTimeout(() => {
                setMessages((prevMessages) =>
                  prevMessages.map((msg) => {
                    if (msg.id === tempId) {
                      // 처리 함수를 사용하여 테이블과 헤더 처리
                      const processedContent = processMarkdownHeadings(msg.content);
                      
                      // 마크다운 제목 올바르게 렌더링되도록 강제 갱신 (내용은 동일하게 유지)
                      return { ...msg, id: `${tempId}-processed`, content: processedContent };
                    }
                    return msg;
                  })
                );
              }, 300); // 300ms 딜레이 후 재처리
            }
          );
        } catch (streamError) {
          console.error(`스트림 연결 오류 (요청 ID: ${requestId}):`, streamError);
          setError('스트리밍 연결 중 오류가 발생했습니다.');
          setIsLoading(false);
        }

        // 컴포넌트가 언마운트될 때 스트림 연결 종료
        return () => {
          if (streamConnection) {
            streamConnection.close();
          }
        };
      } else {
        // 비스트리밍 처리
        console.log(`비스트리밍 요청 시작 (요청 ID: ${requestId})`);
        const response = await chatApi.sendChatMessage(chatRequest);
        console.log(`비스트리밍 응답 수신 (요청 ID: ${requestId})`);
        
        // 마크다운 처리
        let processedContent = response.response;
        
        // processMarkdownHeadings 함수를 이용해 한번에 처리
        processedContent = processMarkdownHeadings(processedContent);
        
        const assistantMessage = {
          role: 'assistant',
          content: processedContent,
          id: `res-${Date.now()}-nonstreaming` // 비스트리밍 메시지는 특별한 ID 사용
        };
        
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
                  console.debug('비스트리밍 응답의 참조문서:', response.sources?.length || 0);
        
        // 전체 소스 문서 정보 보존
        const processedSources = response.sources ? response.sources.map(source => ({
          ...source,
          // source_url 또는 url 필드 확인 (크롤링 시 url 필드로 저장될 수 있음)
          source_url: (source.source_url && 
            (source.source_url.startsWith('http://') || source.source_url.startsWith('https://'))) 
              ? source.source_url 
              : (source.url && 
                (source.url.startsWith('http://') || source.url.startsWith('https://'))) 
                ? source.url 
                : null
        })) : [];
        setSourceDocuments(processedSources);
      }
    } catch (error) {
      console.error('챗봇 응답 에러:', error);
      setError('메시지를 전송하는 중 오류가 발생했습니다.');
    } finally {
      if (!useStreaming) {
        setIsLoading(false);
      }
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setSourceDocuments([]);
  };

  // 메시지 렌더링을 위한 컴포넌트
  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    
    // 마크다운 테이블이 있는지 먼저 확인
    const hasTable = message.role === 'assistant' && message.content && 
                    hasMarkdownTable(message.content);
    
    // 테이블이 없는 경우에만 헤딩 처리
    if (message.role === 'assistant' && message.content && 
        message.content.includes('#') && !hasTable) {
      // 마크다운 형식 관련 설명 텍스트 제거
      message.content = message.content.replace(/이 표는 마크다운 형식으로 작성되었습니다\./g, '');
      message.content = message.content.replace(/마크다운 형식(으로|의|을|을 이용하여|을 사용하여).*?작성/g, '작성');
      
      // 테이블과 헤더를 모두 처리
      if (message.content.includes('|') || message.content.includes('#')) {
        message.content = processMarkdownHeadings(message.content);
      }
    }
    
    // 메시지 내용 처리
    const processContent = () => {
      let content = message.content || '';
      
      if (!content) return '';
      
      // 마크다운 요소들 확인
      const hasTable = hasMarkdownTable(content);
      const hasCode = content.includes('```');
      const hasHeadings = hasMarkdownHeadings(content);
      const hasHtml = hasHtmlTags(content);
      
      // 테이블이 있으면 먼저 HTML로 변환
      if (hasTable) {
        content = parseMarkdownTables(content);
      }
      
      // 마크다운 요소가 있으면 ReactMarkdown으로 처리 (HTML 태그보다 우선)
      if (hasTable || hasCode || hasHeadings || hasHtml) {
        try {
          // remark-gfm 플러그인을 항상 사용하여 테이블과 코드 블록 처리
          const remarkPlugins = [remarkGfm];
          
          return (
            <MarkdownErrorBoundary content={content}>
              <ReactMarkdown
                remarkPlugins={remarkPlugins}
                rehypePlugins={[rehypeRaw]} // rehypeRaw를 항상 사용하여 HTML 태그 렌더링
                components={{
                  h1: ({ level, children }) => <h1>{children}</h1>,
                  h2: ({ level, children }) => <h2>{children}</h2>,
                  h3: ({ level, children }) => <h3>{children}</h3>,
                  h4: ({ level, children }) => <h4>{children}</h4>,
                  h5: ({ level, children }) => <h5>{children}</h5>,
                  h6: ({ level, children }) => <h6>{children}</h6>,
                  table: ({children, ...props}) => (
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      margin: '16px 0',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} {...props}>
                      {children}
                    </table>
                  ),
                  thead: ({children, ...props}) => (
                    <thead style={{backgroundColor: '#f5f5f5'}} {...props}>
                      {children}
                    </thead>
                  ),
                  th: ({children, ...props}) => (
                    <th style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      borderBottom: '2px solid #e0e0e0',
                      fontWeight: 'bold'
                    }} {...props}>
                      {children}
                    </th>
                  ),
                  td: ({children, ...props}) => (
                    <td style={{
                      padding: '8px 16px',
                      borderBottom: '1px solid #eeeeee'
                    }} {...props}>
                      {children}
                    </td>
                  ),
                  tr: ({children, ...props}) => (
                    <tr {...props}>
                      {children}
                    </tr>
                  ),
                  code({node, inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || '')
                    
                    // 들여쓰기 보존을 위한 코드 전처리
                    let codeString = String(children);
                    
                    // 단순하게 앞뒤 개행만 제거하고 내부 공백은 모두 보존
                    codeString = codeString.replace(/^\n+/, '').replace(/\n+$/, '');
                    
                    // 들여쓰기 복원 로직 추가
                    const lines = codeString.split('\n');
                    let processedLines = [];
                    let currentIndentLevel = 0;
                    
                    for (let i = 0; i < lines.length; i++) {
                      const line = lines[i];
                      const trimmedLine = line.trim();
                      
                      // 빈 줄은 그대로 유지
                      if (trimmedLine === '') {
                        processedLines.push('');
                        continue;
                      }
                      
                      // 들여쓰기 레벨 조정
                      if (trimmedLine.includes('{')) {
                        // 중괄호가 있으면 현재 레벨로 들여쓰기 후 레벨 증가
                        processedLines.push('  '.repeat(currentIndentLevel) + trimmedLine);
                        if (trimmedLine.endsWith('{') || (trimmedLine.includes('{') && !trimmedLine.includes('}'))) {
                          currentIndentLevel++;
                        }
                      } else if (trimmedLine.includes('}')) {
                        // 닫는 중괄호가 있으면 레벨 감소 후 들여쓰기
                        if (trimmedLine.startsWith('}')) {
                          currentIndentLevel = Math.max(0, currentIndentLevel - 1);
                        }
                        processedLines.push('  '.repeat(currentIndentLevel) + trimmedLine);
                        if (trimmedLine.endsWith('}') && !trimmedLine.startsWith('}')) {
                          currentIndentLevel = Math.max(0, currentIndentLevel - 1);
                        }
                      } else if (trimmedLine.includes('[')) {
                        // 배열 시작
                        processedLines.push('  '.repeat(currentIndentLevel) + trimmedLine);
                        if (trimmedLine.endsWith('[') || (trimmedLine.includes('[') && !trimmedLine.includes(']'))) {
                          currentIndentLevel++;
                        }
                      } else if (trimmedLine.includes(']')) {
                        // 배열 끝
                        if (trimmedLine.startsWith(']')) {
                          currentIndentLevel = Math.max(0, currentIndentLevel - 1);
                        }
                        processedLines.push('  '.repeat(currentIndentLevel) + trimmedLine);
                      } else {
                        // 일반 라인은 현재 레벨로 들여쓰기
                        processedLines.push('  '.repeat(currentIndentLevel) + trimmedLine);
                      }
                    }
                    
                    codeString = processedLines.join('\n');
                    
                    // 복사 기능 함수
                    const handleCopyCode = async () => {
                      try {
                        await navigator.clipboard.writeText(codeString);
                        // 간단한 피드백 (필요시 토스트 메시지로 교체 가능)
                        const button = document.activeElement;
                        const originalText = button.textContent;
                        button.textContent = '복사됨!';
                        button.style.backgroundColor = '#4caf50';
                        button.style.color = '#ffffff';
                        setTimeout(() => {
                          button.textContent = originalText;
                          button.style.backgroundColor = '#f5f5f5';
                          button.style.color = '#666';
                        }, 1500);
                      } catch (err) {
                        console.error('복사 실패:', err);
                      }
                    };
                    
                    return !inline && match ? (
                      <div style={{ position: 'relative', marginBottom: '16px' }}>
                        <button
                          onClick={handleCopyCode}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 10,
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#666',
                            fontFamily: 'inherit',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#e0e0e0';
                            e.target.style.color = '#333';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.color = '#666';
                          }}
                        >
                          복사
                        </button>
                        <SyntaxHighlighter
                          style={oneLight}
                          language={match[1]}
                          PreTag="div"
                          showLineNumbers={true}
                          wrapLines={false}
                          wrapLongLines={false}
                          useInlineStyles={true}
                          customStyle={{
                            margin: 0,
                            padding: '16px',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e1e1e1',
                            whiteSpace: 'pre',
                            overflowX: 'auto',
                            tabSize: 4
                          }}
                          lineNumberStyle={{
                            color: '#999',
                            backgroundColor: 'transparent',
                            paddingRight: '16px',
                            marginRight: '16px',
                            borderRight: '1px solid #e1e1e1',
                            minWidth: '40px'
                          }}
                          codeTagProps={{
                            style: {
                              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                              fontSize: '14px',
                              whiteSpace: 'pre',
                              backgroundColor: 'transparent'
                            }
                          }}
                          {...props}
                        >
                          {codeString}
                        </SyntaxHighlighter>
                      </div>
                    ) : (
                      <code className={className} style={{
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        fontSize: '14px',
                        backgroundColor: '#f8f8f8',
                        color: '#d63384',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                        whiteSpace: 'pre'
                      }} {...props}>
                        {children}
                      </code>
                    )
                  }
                }}
              >
                {content}
              </ReactMarkdown>
            </MarkdownErrorBoundary>
          );
        } catch (error) {
          console.error('ReactMarkdown 렌더링 에러:', error);
          // 에러 발생 시 대체 렌더링
          return (
            <div>
              <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '0.8rem' }}>
                ⚠️ 마크다운 렌더링 중 오류가 발생했습니다. 원본 텍스트를 표시합니다.
              </Typography>
              <Typography variant="body1" component="div" style={{ whiteSpace: 'pre-wrap' }}>
                {content}
              </Typography>
            </div>
          );
        }
      }
      
      // 일반 텍스트는 그대로 표시
      return (
        <Typography variant="body1" component="div" style={{ whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      );
    };
    
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
        }}
      >
        {/* 카카오톡에서 온 메시지인 경우 상단에 표시 */}
        {message.isFromKakao && (
          <Box sx={{ width: '100%', mb: 1 }}>
            <Alert 
              severity="info" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <AlertTitle>카카오톡에서 전송된 {isUser ? '질문' : '답변'}</AlertTitle>
              {message.timestamp && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(message.timestamp).toLocaleString('ko-KR')}
                </Typography>
              )}
              {!isUser && message.processingTime && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  처리시간: {message.processingTime}초
                </Typography>
              )}
            </Alert>
          </Box>
        )}
        
        <Paper
          elevation={1}
          sx={{
            p: 2,
            maxWidth: '80%',
            backgroundColor: message.isFromKakao 
              ? (isUser ? '#fff3e0' : '#e8f5e8')  // 카카오톡 메시지는 다른 색상
              : (isUser ? '#e3f2fd' : '#f5f5f5'),
            borderRadius: isUser ? '16px 16px 0 16px' : '16px 16px 16px 0',
            border: message.isFromKakao ? '2px solid #ff9800' : 'none',
          }}
        >
          <Typography variant="body1" component="div" className="markdown-content">
            {processContent()}
          </Typography>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: 'calc(100vh - 120px)',
      width: '100%',
      maxWidth: '100%',
      padding: '24px 16px',
      margin: 0,
      boxSizing: 'border-box'
    }}>
      {/* 카카오톡 연동 상태 표시 */}
      {isFromKakao && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          action={
            kakaoAnswerId && (
              <Typography variant="caption" color="text.secondary">
                ID: {kakaoAnswerId.substring(0, 8)}...
              </Typography>
            )
          }
        >
          <AlertTitle>카카오톡 연동 모드</AlertTitle>
          카카오톡에서 전송된 질문의 답변을 불러왔습니다. 이어서 추가 질문을 하실 수 있습니다.
        </Alert>
      )}
      
      {/* 카카오톡 데이터 로딩 중 표시 */}
      {isLoadingKakaoData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            카카오톡 답변 데이터를 불러오는 중...
          </Box>
        </Alert>
      )}
      
      {/* 지식베이스와 LLM 모델 선택 UI */}
      <Box sx={{ mb: 1, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={isKBLoading}>
              <InputLabel>지식베이스</InputLabel>
              <Select
                multiple
                value={selectedKBs}
                label="지식베이스"
                onChange={(e) => setSelectedKBs(e.target.value)}
                renderValue={(selected) => selected.length > 0 ? `${selected.length}개 선택됨` : ''}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250
                    }
                  }
                }}
              >
                {(knowledgeBases || []).map((kb) => (
                  <MenuItem key={kb.kb_id} value={kb.kb_id}>
                    {kb.name}
                  </MenuItem>
                ))}
              </Select>
               
              {/* 선택된 지식베이스를 별도로 표시 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {(selectedKBs || []).map((value) => {
                  const kb = knowledgeBases.find(kb => kb.kb_id === value);
                  return (
                    <Chip 
                      key={value} 
                      label={kb ? kb.name : value} 
                      size="small"
                      onDelete={() => {
                        const newSelected = (selectedKBs || []).filter(id => id !== value);
                        setSelectedKBs(newSelected);
                      }}
                      sx={{ 
                        m: 0.5, 
                        '& .MuiChip-label': { 
                          fontSize: '1rem',
                          fontWeight: 'medium',
                          color: '#333',
                          padding: '0 10px'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={isModelsLoading}>
              <InputLabel>LLM 모델</InputLabel>
              <Select
                value={selectedModel}
                label="LLM 모델"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {(llmModels || []).map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={useStreaming}
                  onChange={(e) => setUseStreaming(e.target.checked)}
                  size="small"
                />
              }
              label="스트리밍"
              sx={{ 
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleClearChat}
              size="small"
              sx={{ 
                borderColor: '#666',
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                  color: '#333',
                }
              }}
            >
              대화 초기화
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} sx={{ flexGrow: 1, mb: 2, height: 'calc(70vh - 120px)' }}>
        {/* Chat Messages Area */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Paper
            elevation={3}
            sx={{
              height: '100%',
              p: 2,
              overflowY: 'auto',
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
            }}

          >
            {(messages || []).length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="h6" gutterBottom>
                  AI 질의응답을 시작하세요
                </Typography>
              </Box>
            ) : (
              (messages || []).map(renderMessage)
            )}
            
            {isLoading && useStreaming && (
              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  응답 생성 중...
                </Typography>
              </Box>
            )}
            
            {error && <ErrorMessage message={error} />}
            
            <div ref={chatEndRef} />
          </Paper>
        </Grid>

        {/* Reference Documents Area - Always visible */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Paper elevation={2} sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="small" sx={{ mr: 1 }} />
              참조 문서
            </Typography>
            
            {(sourceDocuments || []).length > 0 ? (
              // Show reference documents when available
              (sourceDocuments || []).map((source, index) => {
                // URL 유효성 검사 - source_url 또는 url 필드 확인 (크롤링 시 url 필드로 저장될 수 있음)
                const sourceUrl = source.source_url || source.url;
                const hasValidUrl = Boolean(sourceUrl && 
                  (sourceUrl.startsWith('http://') || 
                   sourceUrl.startsWith('https://')));
                
                return (
                <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1,
                    }}>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        width: 'calc(100% - 120px)',
                      }}>
                        <Typography 
                          variant="body2" 
                          color="primary.main" 
                          sx={{ 
                            maxWidth: '100%', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mr: 1
                          }}
                        >
                          {hasValidUrl ? (
                            <a 
                              href={sourceUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#1976d2', 
                                textDecoration: 'none', 
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.textDecoration = 'underline';
                                e.target.style.color = '#1565c0';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.textDecoration = 'none';
                                e.target.style.color = '#1976d2';
                              }}
                              onClick={(e) => {
                                // URL 클릭 추적 (필요시)
                              }}
                            >
                              {source.document_name} {source.page_number && `(페이지: ${source.page_number})`}
                              <LaunchIcon sx={{ fontSize: '0.8rem', ml: 0.5, verticalAlign: 'middle' }} />
                            </a>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <span style={{ color: '#666' }}>
                                {source.document_name} {source.page_number && `(페이지: ${source.page_number})`}
                              </span>
                              {!sourceUrl && (
                                <Tooltip title="원문 링크가 없습니다" placement="top">
                                  <InfoIcon sx={{ fontSize: '0.8rem', ml: 0.5, color: '#999' }} />
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      
                      {/* 정확도 칩 - 고정 너비로 배치 */}
                      <Box sx={{ 
                        width: '110px', 
                        flexShrink: 0,
                        textAlign: 'right'
                      }}>
                        <Chip 
                          label={`정확도: ${Math.round(source.score * 100)}%`} 
                          size="small" 
                          color={source.score > 0.8 ? "success" : "primary"}
                          sx={{ 
                            '.MuiChip-label': { 
                              padding: '0 8px',
                              fontWeight: 'medium'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '0.825rem' }}>
                      "{source.snippet}"
                    </Typography>
                    
                    {/* URL 정보 표시 (디버깅용 - 나중에 제거 가능) */}
                    {hasValidUrl && (
                      <Typography variant="caption" sx={{ 
                        fontSize: '0.7rem', 
                        color: '#666', 
                        mt: 0.5,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        🔗 {sourceUrl}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )})
            ) : (
              // Show placeholder when no documents are available
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: 'calc(100% - 40px)', 
                  color: 'text.secondary',
                  borderRadius: 1,
                  border: '1px dashed #ccc',
                  p: 2
                }}
              >
                <Typography variant="body2" color="text.secondary" align="center">
                  질문을 입력하면 관련된 참조 문서가 이 곳에 표시됩니다.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 1 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="질문을 입력하세요..."
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading || !(selectedKBs || []).length}
              multiline
              maxRows={3}
              sx={{ mr: 1 }}

            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !input.trim() || !(selectedKBs || []).length}
              endIcon={isLoading && !useStreaming ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                whiteSpace: 'nowrap',
                color: '#ffffff !important',
                fontWeight: '600',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important'
                }
              }}
            >
              전송
            </Button>
          </Box>
        </form>
      </Paper>

      {/* 경고 알림창 */}
      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderTop: '4px solid #f44336',
            borderRadius: '8px',
            maxWidth: '400px',
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
          알림
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 20px 24px' }}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            autoFocus
            sx={{ 
              backgroundColor: '#f44336', 
              '&:hover': { backgroundColor: '#d32f2f' } 
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatPage; 